import { APP_INITIALIZER, ErrorHandler, Injectable, InjectionToken, Provider, inject } from '@angular/core';
import { createApiUrls } from './api-urls';

export interface AppErrorLoggingConfig {
  apiBaseUrl?: string;
  endpoint: string;
  tokenKey: string;
  source: string;
  app: string;
}

interface AppErrorPayload {
  fingerprint: string;
  source: string;
  severity: 'error';
  error_class: string;
  message: string;
  stack: string;
  route: string;
  user_agent: string;
  metadata: Record<string, string>;
}

export const APP_ERROR_LOGGING_CONFIG = new InjectionToken<AppErrorLoggingConfig>('APP_ERROR_LOGGING_CONFIG');

@Injectable()
export class AppErrorLoggingService {
  private readonly config = inject(APP_ERROR_LOGGING_CONFIG);
  private readonly apiUrls = createApiUrls({ apiBaseUrl: this.config.apiBaseUrl });
  private isReporting = false;
  private isInitialized = false;

  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    window.addEventListener('unhandledrejection', (event) => {
      this.capture(event.reason, { event_type: 'unhandledrejection' });
    });
  }

  capture(error: unknown, metadata: Record<string, string> = {}): void {
    const token = this.authToken();
    if (!token || this.isReporting) {
      return;
    }

    this.isReporting = true;
    void fetch(this.apiUrls.v1(this.config.endpoint), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ app_error: this.payloadFor(error, metadata) })
    }).catch(() => undefined).finally(() => {
      this.isReporting = false;
    });
  }

  private payloadFor(error: unknown, metadata: Record<string, string>): AppErrorPayload {
    const normalized = this.normalizeError(error);
    return {
      ...normalized,
      fingerprint: this.fingerprint(normalized),
      source: this.config.source,
      severity: 'error',
      route: this.currentRoute(),
      user_agent: navigator.userAgent,
      metadata: {
        app: this.config.app,
        href: window.location.href,
        ...metadata
      }
    };
  }

  private normalizeError(error: unknown): Pick<AppErrorPayload, 'error_class' | 'message' | 'stack'> {
    if (error instanceof Error) {
      return {
        error_class: error.name || 'Error',
        message: error.message || 'Unknown app error',
        stack: error.stack || ''
      };
    }

    return {
      error_class: typeof error,
      message: this.messageFor(error),
      stack: ''
    };
  }

  private messageFor(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    try {
      return JSON.stringify(error);
    } catch {
      return 'Unknown app error';
    }
  }

  private fingerprint(error: Pick<AppErrorPayload, 'error_class' | 'message' | 'stack'>): string {
    const input = [this.config.source, error.error_class, error.message, error.stack.split('\n')[0] || '', this.currentRoute()].join('|');
    let hash = 0;

    for (let index = 0; index < input.length; index += 1) {
      hash = Math.imul(31, hash) + input.charCodeAt(index);
      hash |= 0;
    }

    return Math.abs(hash).toString(16);
  }

  private currentRoute(): string {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  private authToken(): string {
    try {
      return localStorage.getItem(this.config.tokenKey) || '';
    } catch {
      return '';
    }
  }
}

@Injectable()
export class AppErrorHandler implements ErrorHandler {
  private readonly appErrorLogging = inject(AppErrorLoggingService);

  handleError(error: unknown): void {
    this.appErrorLogging.capture(error, { event_type: 'angular_error' });
    console.error(error);
  }
}

export function provideAppErrorLogging(config: AppErrorLoggingConfig): Provider[] {
  return [
    { provide: APP_ERROR_LOGGING_CONFIG, useValue: config },
    AppErrorLoggingService,
    { provide: ErrorHandler, useClass: AppErrorHandler },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [AppErrorLoggingService],
      useFactory: (appErrorLogging: AppErrorLoggingService) => () => appErrorLogging.initialize()
    }
  ];
}
